if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          select: "select",
        };
        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
      }

      connectedCallback() {
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm ? this.mainProductForm.querySelector(".m-add-to-cart") : null;
        this.mainDynamicCheckout = this.mainProductForm.querySelector(this.selectors.buyNowBtn);
        this.disableSelectedVariantDefault = this.mainProduct.dataset.disableSelectedVariantDefault === "true" || false;
        this.domNodes = queryDomNodes(this.selectors, this.container);

        this.init();
      }

      init() {
        if (!this.mainAddToCart) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        this.variantData = this.getVariantData();

        const headerHeight = MinimogSettings.headerHeight || 66;

        // Show only after the main product form has been scrolled PAST (its
        // bottom sits above the header). Scroll-driven rather than an
        // IntersectionObserver: observers can miss fast scroll-jumps that skip
        // the threshold crossing, leaving the bar stuck in a stale state.
        // Also fixes the page-top flash — at the top the form is below the
        // fold, so the bar stays hidden. (2026-07-20)
        this.evaluateSticky = () => {
          const target = this.observeTarget || this.mainProductForm;
          if (!target) return;
          const scrolledPast = target.getBoundingClientRect().bottom < headerHeight;
          this.container.style.setProperty("--m-translate-y", scrolledPast ? 0 : "100%");
          document.documentElement.classList[scrolledPast ? "add" : "remove"]("stick-atc-show");
        };
        let stickyRafPending = false;
        const onScrollSticky = () => {
          if (stickyRafPending) return;
          stickyRafPending = true;
          requestAnimationFrame(() => {
            stickyRafPending = false;
            this.evaluateSticky();
          });
        };
        window.addEventListener("scroll", onScrollSticky, { passive: true });
        window.addEventListener("resize", onScrollSticky);

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        const { prodTitle, mainImage } = this.domNodes;
        prodTitle.addEventListener("click", () => __scrollToTop());
        mainImage.addEventListener("click", () => __scrollToTop());

        this.handleCustomFields();

        this.setObserveTarget();
        this.syncWithMainProductForm();

        const mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        mql.onchange = this.setStickyAddToCartHeight.bind(this);
        this.setStickyAddToCartHeight();

        this.domNodes.select.addEventListener("change", (e) => {
          const { target } = e;
          const variantPicker = this.mainProduct.querySelector('variant-picker');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
          this.currentVariant ? this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut) : this.toggleAddButton(true, window.MinimogStrings.unavailable);

          const selectedOption = target.options[target.selectedIndex];
          const selectedOptionIds = selectedOption.dataset.options.split(',').filter(id => id);

          if (variantPicker) {
            selectedOptionIds.forEach((optionId) => {
              const input = variantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
              const { tagName } = input;
              switch (tagName) {
                case "OPTION":
                  const inputParent = input.parentNode; // select tag
                  inputParent.value = input.value;
                  break;
                case "INPUT":
                  input.checked = true;
                  break;
              }
            });
            variantPicker.dispatchEvent(new Event("change"));
          }
        });
      }

      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      setObserveTarget() {
        this.observeTarget = this.mainProductForm;
        if (this.evaluateSticky) this.evaluateSticky();
      }

      setUnavailable() {
        const productForm = this.querySelector('.m-product-form');
        const addButton = productForm.querySelector('[name="add"]');

        if (addButton) {
          this.toggleAddButton(true, window.MinimogStrings.unavailable);
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.m-product-form');
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      handleDisableSelectedVariantDefault() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        const pickerFields = this.querySelector('.m-product-option--dropdown-select');

        pickerFields.value = '';
        this.setUnavailable();
      }

      setStickyAddToCartHeight() {
        // Measure the CONTAINER, not <sticky-atc> — the mobile ship line
        // (.ss-sticky-ship) sits above <sticky-atc> inside the container, so
        // measuring `this` under-reports the bar height. (2026-08-11)
        const host = this.container || this;
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", host.offsetHeight + "px");
        window.MinimogSettings.stickyAddToCartHeight = host.offsetHeight;
      }

      // Repaint the sticky bar's price + stock/ship badge for the newly selected
      // variant. (2026-08-11)
      //
      // product-info.js re-fetches the whole main-product section on a variant
      // change but only copies #Price-…, #Inventory-…, #Sku-…, #Availability-…,
      // #Volume-… and #PricePerItem-… out of it — the sticky bar in that
      // response is thrown away, so its price and low-stock state used to go
      // stale the moment you changed variant.
      //
      // Source the markup from that same response rather than cloning the live
      // #Price- block: the main block renders product-prices with its own
      // price_text_size and sale-badge settings, so cloning it would drag those
      // classes into the sticky bar and change its type size mid-session. The
      // re-fetched sticky bar is already rendered with the sticky bar's own
      // params — an exact drop-in.
      //
      // e.data.sectionId is the id used in the FETCHED markup;
      // this.mainProduct.dataset.sectionId is the local one. They differ.
      repaintFromSection(html, fetchedSectionId) {
        if (!html || !fetchedSectionId) return;
        const src = html.getElementById
          ? html.getElementById(`Sticky-Addtocart-${fetchedSectionId}`)
          : html.querySelector(`#Sticky-Addtocart-${fetchedSectionId}`);
        if (!src || !this.container) return;

        // Desktop: price + badge under the product title.
        const srcPrice = src.querySelector(".m-sticky-addtocart--title .m-price");
        const destPrice = this.querySelector(".m-sticky-addtocart--title .m-price");
        if (srcPrice && destPrice) destPrice.innerHTML = srcPrice.innerHTML;

        // Mobile: badge-only line above the controls.
        const srcShip = src.querySelector(".ss-sticky-ship");
        const destShip = this.container.querySelector(".ss-sticky-ship");
        if (srcShip && destShip) destShip.innerHTML = srcShip.innerHTML;

        // Freshly rendered .shipping-estimate spans come back empty;
        // shipping-estimate.js refills every span on the variant:changed event
        // product-info.js fires immediately after this one.
        this.setStickyAddToCartHeight();
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');
        window.MinimogEvents && window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          const isMainProduct = e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;
          this.currentVariant = e.data.variant;
          if (this.currentVariant) {
            variantInput.value = e.data.variant.id;
            this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut)
          } else {
            variantInput.value = '';
            this.toggleAddButton(true, window.MinimogStrings.unavailable)
          }
          this.repaintFromSection(e.data.html, e.data.sectionId);
        });
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } = this.domNodes;

        let hasCustomFieldRequired = false;
        const customFields = document.querySelectorAll(".m-main-product--info .m-product-custom-field");
        let customFieldFirst = customFields[0];
        customFields &&
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field.value == "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

        hasCustomFieldRequired &&
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = validateForm(this.mainProductInfo);
              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                __scrollToTop(this.mainProductInfo, () => this.mainDynamicCheckout.click());
              }
            },
            true
          );
        }
        if (foxkitBtn) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        }
      }
    }
  );
}
